/**
 * TradingView Advanced Charts Component
 *
 * This component loads the TradingView charting library dynamically
 * and renders an advanced chart with full technical analysis capabilities.
 *
 * Features:
 * - 100+ technical indicators
 * - Drawing tools
 * - Multiple timeframes (1D, 1W, 1M)
 * - Studies and templates
 * - Professional trading interface
 *
 * Usage:
 *   <TradingViewChart dataId="uuid-here" type="candlestick" />
 */

'use client';

import React, { useEffect, useRef, useState } from 'react';
import { TradingViewDatafeed } from '@/lib/tradingview-datafeed';
import { Skeleton } from '@/components/ui/skeleton';
import type { SRLevels } from '@/components/message-content';
import { getColorForIndex } from '@/lib/comparison-chart-utils';
import { useDatapacks } from '@/contexts/DatapackContext';
import type { ChartDataPoint } from '@/lib/chart-utils';
import { studyNameMap } from '@/lib/tradingview-studies';
import { ChartContainer } from '@/components/chart-container';

interface TradingViewChartProps {
  /** Chart data identifier */
  dataId: string;
  /** Chart type - candlestick or line (default: candlestick) */
  type?: 'candlestick' | 'line';
  /** Ticker symbol for display (e.g., "AAPL", "BTC-USD") */
  ticker?: string;
  /** Comma-separated list of technical indicators to apply (e.g., "MACD,RSI,BB") */
  studies?: string;
  /** Session ID for backend API calls */
  sessionId?: string;
  /** User ID for backend API calls */
  userId?: string;
  /** Support/Resistance levels to draw on chart */
  supportResistance?: SRLevels | null;
  /** Start date for initial visible range (e.g., "2024-11-04") */
  startDate?: string;
  /** End date for initial visible range (e.g., "2025-11-04") */
  endDate?: string;
  /** Comparison type for multi-asset charts: 'price' (normalized %) or 'indicator' (raw values) */
  comparison?: 'price' | 'indicator';
  /** Array of ticker symbols for comparison charts - for display/legend (e.g., ["BTC-USD", "ETH-USD"]) */
  tickers?: string[];
  /** Array of datapack UUIDs for comparison charts - each UUID points to a separate OHLC datapack */
  dataIds?: string[];
  /** Indicator name for indicator comparison charts (e.g., "RSI", "MACD") */
  indicator?: string;
}

type ChartState = 'loading' | 'error' | 'success';

/**
 * Renders a comparison chart with multiple assets
 * Fetches individual datapacks for each asset and uses TradingView's comparison feature
 */
async function renderComparisonChart(
  widget: TradingViewWidget,
  comparisonType: 'price' | 'indicator',
  tickers: string[],
  dataIds: string[],
  indicator?: string,
  sessionId?: string,
  userId?: string,
  prefetchedDatapacks?: Map<string, { id: string; data: ChartDataPoint[] }>
) {
  try {
    console.log(`[renderComparisonChart] Fetching ${dataIds.length} datapacks for comparison...`);
    console.log(`[renderComparisonChart] Tickers: ${tickers.join(', ')}`);
    console.log(`[renderComparisonChart] Data IDs: ${dataIds.join(', ')}`);

    // Fetch all datapacks in parallel (check context first, then API)
    const fetchPromises = dataIds.map(async (datapackId, index) => {
      console.log(`[renderComparisonChart] Fetching datapack ${index + 1}/${dataIds.length}: ${datapackId}`);

      // Check if data is prefetched (historical session)
      const prefetched = prefetchedDatapacks?.get(datapackId);
      if (prefetched) {
        console.log(`[renderComparisonChart] Using prefetched data for ${datapackId}`);
        return {
          datapackId,
          ticker: tickers[index] || `Asset ${index + 1}`,
          data: prefetched.data,
          color: getColorForIndex(index),
        };
      }

      // Not prefetched - fetch from API (active session)
      const response = await fetch(`/api/chart-data/${datapackId}`, {
        headers: {
          ...(sessionId && { 'X-Session-Id': sessionId }),
          ...(userId && { 'X-User-Id': userId }),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch datapack ${datapackId}: ${response.statusText}`);
      }

      const result = await response.json();
      return {
        datapackId,
        ticker: tickers[index] || `Asset ${index + 1}`,
        data: result.data,
        color: getColorForIndex(index),
      };
    });

    const datapacks = await Promise.all(fetchPromises);
    console.log(`[renderComparisonChart] ✓ Fetched ${datapacks.length} datapacks successfully`);

    // Log summary for each datapack
    datapacks.forEach((dp, i) => {
      console.log(`  [${i + 1}] ${dp.ticker}: ${dp.data.length} data points (${dp.color})`);
    });

    // Get chart instance
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const chart = widget.activeChart() as any;

    // Remove volume indicator for comparison charts (confusing when comparing multiple assets)
    try {
      const allStudies = chart.getAllStudies();
      console.log('[renderComparisonChart] All studies:', allStudies);

      // Find and remove Volume study
      const volumeStudy = allStudies.find((study: { id: string; name: string }) =>
        study.name === 'Volume' || study.name.toLowerCase().includes('volume')
      );

      if (volumeStudy) {
        chart.removeEntity(volumeStudy.id);
        console.log('[renderComparisonChart] Removed volume study:', volumeStudy.id);
      }
    } catch (error) {
      console.warn('[renderComparisonChart] Could not remove volume study:', error);
    }

    // TradingView Advanced Charts only supports price comparison (not indicator comparison)
    // Use Compare study to overlay multiple symbols with percentage normalization
    console.log('[renderComparisonChart] Price comparison - using Compare study');

    // The first datapack is already loaded as the main symbol
    // Add the remaining datapacks as comparison symbols
    for (let i = 1; i < datapacks.length; i++) {
      const dp = datapacks[i];
      const displayName = `${dp.ticker} Historical Prices`;
      console.log(`[renderComparisonChart] Adding comparison symbol ${i}/${datapacks.length - 1}: ${displayName}`);

      try {
        // Use friendly display name instead of datapackId for better chart labels
        console.log(`[renderComparisonChart] Creating Compare study with symbol: ${displayName}`);
        const studyId = chart.createStudy(
          'Compare',
          false, // forceOverlay
          false, // lock
          { symbol: displayName }, // Use friendly display name registered in datafeed
          {
            // Override default styles
            'Plot.color': dp.color,
            'Plot.linewidth': 2,
          }
        );

        console.log(`[renderComparisonChart] ✓ Added comparison study ${studyId} for ${displayName}`);
      } catch (error) {
        console.error(`[renderComparisonChart] Failed to add comparison symbol ${displayName}:`, error);
      }
    }

    // Enable percentage mode for normalized price comparison
    try {
      chart.applyOverrides({
        'mainSeriesProperties.priceAxisProperties.percentage': true,
        'mainSeriesProperties.priceAxisProperties.percentageDisabled': false,
      });
    } catch (error) {
      console.warn('[renderComparisonChart] Could not set percentage mode:', error);
    }

    console.log('[renderComparisonChart] ✓ Comparison chart rendered successfully');
  } catch (error) {
    console.error('[renderComparisonChart] Failed to render comparison chart:', error);
  }
}

/**
 * Draw support and resistance lines on TradingView chart
 */
function drawSupportResistanceLines(chart: unknown, levels: SRLevels | null) {
  if (!levels) return;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tvChart = chart as any;

  console.log('[TradingViewChart] Drawing support/resistance lines:', {
    resistance: levels.resistance.length,
    support: levels.support.length
  });

  // Draw resistance lines (purple/magenta)
  levels.resistance.forEach((level, index) => {
    const lineWidth = level.strength >= 0.8 ? 3 : level.strength >= 0.5 ? 2 : 1;

    try {
      tvChart.createShape(
        { time: tvChart.getVisibleRange().from, price: level.price },
        {
          shape: 'horizontal_line',
          overrides: {
            linecolor: '#9B59B6',      // Purple
            linewidth: lineWidth,
            linestyle: 0,               // Solid
            showLabel: true,
            text: `R${index + 1}: $${level.price.toFixed(2)}`,
            textcolor: '#9B59B6',
            showPrice: false,           // Hide Y-axis price label
          },
          lock: true,
          disableSelection: true,
        }
      );
      console.log(`[TradingViewChart] ✓ Drew resistance line R${index + 1} at $${level.price}`);
    } catch (error) {
      console.error(`[TradingViewChart] Failed to draw resistance line:`, error);
    }
  });

  // Draw support lines (blue)
  levels.support.forEach((level, index) => {
    const lineWidth = level.strength >= 0.8 ? 3 : level.strength >= 0.5 ? 2 : 1;

    try {
      tvChart.createShape(
        { time: tvChart.getVisibleRange().from, price: level.price },
        {
          shape: 'horizontal_line',
          overrides: {
            linecolor: '#3498DB',      // Blue
            linewidth: lineWidth,
            linestyle: 0,
            showLabel: true,
            text: `S${index + 1}: $${level.price.toFixed(2)}`,
            textcolor: '#3498DB',
            showPrice: false,           // Hide Y-axis price label
          },
          lock: true,
          disableSelection: true,
        }
      );
      console.log(`[TradingViewChart] ✓ Drew support line S${index + 1} at $${level.price}`);
    } catch (error) {
      console.error(`[TradingViewChart] Failed to draw support line:`, error);
    }
  });
}


/**
 * TradingView Chart Component
 * Dynamically loads TradingView library and renders advanced chart
 */
export function TradingViewChart({
  dataId,
  type = 'candlestick',
  ticker,
  studies,
  sessionId,
  userId,
  supportResistance,
  startDate,
  endDate,
  comparison,
  tickers,
  dataIds,
  indicator,
}: TradingViewChartProps) {
  // Get prefetched datapacks from context (for historical sessions)
  const { datapacks: contextDatapacks } = useDatapacks();

  // Build prefetched datapacks map for datafeed (check all relevant dataIds)
  const prefetchedDatapacks = React.useMemo(() => {
    const allDataIds = [dataId, ...(dataIds || [])];
    console.log(`[TradingViewChart] 🔍 Looking for prefetched data for:`, allDataIds);
    console.log(`[TradingViewChart] 🔍 Context has ${contextDatapacks.size} datapacks`);

    const prefetched = new Map<string, ChartDataPoint[]>();

    allDataIds.forEach(id => {
      const datapack = contextDatapacks.get(id);
      if (datapack) {
        console.log(`[TradingViewChart] ✅ Found prefetched datapack for ${id}`, datapack);
        prefetched.set(id, datapack.data);
      } else {
        console.log(`[TradingViewChart] ❌ No prefetched datapack for ${id}`);
      }
    });

    console.log(`[TradingViewChart] 📊 Built prefetched map with ${prefetched.size} entries`);
    return prefetched.size > 0 ? prefetched : undefined;
  }, [dataId, dataIds, contextDatapacks]);

  // Normalize dataIds and tickers to ensure they're arrays (defensive)
  const normalizedDataIds = React.useMemo(() => {
    if (!dataIds) return undefined;
    if (Array.isArray(dataIds)) return dataIds;
    // If it's a string (shouldn't happen but defensive), split it
    if (typeof dataIds === 'string') {
      console.warn('[TradingViewChart] dataIds received as string, converting to array:', dataIds);
      return (dataIds as unknown as string).split(',').map(id => id.trim());
    }
    return undefined;
  }, [dataIds]);

  const normalizedTickers = React.useMemo(() => {
    if (!tickers) return undefined;
    if (Array.isArray(tickers)) return tickers;
    // If it's a string (shouldn't happen but defensive), split it
    if (typeof tickers === 'string') {
      console.warn('[TradingViewChart] tickers received as string, converting to array:', tickers);
      return (tickers as unknown as string).split(',').map(t => t.trim());
    }
    return undefined;
  }, [tickers]);

  // Extract joined strings for useEffect dependencies (avoid complex expressions)
  const tickersKey = React.useMemo(() => normalizedTickers?.join(','), [normalizedTickers]);
  const dataIdsKey = React.useMemo(() => normalizedDataIds?.join(','), [normalizedDataIds]);

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<TradingViewWidget | null>(null);
  const datafeedRef = useRef<TradingViewDatafeed | null>(null); // Store datafeed reference for accessing cached bars
  const supportResistanceRef = useRef<SRLevels | null>(supportResistance);
  const tickersRef = useRef<string[] | undefined>(normalizedTickers);
  const dataIdsRef = useRef<string[] | undefined>(normalizedDataIds);
  const [state, setState] = useState<ChartState>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Update ref when supportResistance changes without triggering re-initialization
  useEffect(() => {
    supportResistanceRef.current = supportResistance;
  }, [supportResistance]);

  // Update refs when tickers/dataIds change without triggering re-initialization
  useEffect(() => {
    tickersRef.current = normalizedTickers;
  }, [normalizedTickers]);

  useEffect(() => {
    dataIdsRef.current = normalizedDataIds;
  }, [normalizedDataIds]);

  useEffect(() => {
    // Ensure we're on the client side
    if (typeof window === 'undefined' || !chartContainerRef.current) {
      return;
    }

    const loadTradingView = async () => {
      try {
        setState('loading');

        // Load TradingView library dynamically if not already loaded
        if (!window.TradingView) {
          console.log('[TradingViewChart] Loading TradingView library...');

          const script = document.createElement('script');
          script.src = '/charting_library/charting_library.standalone.js';
          script.async = true;

          await new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = () => reject(new Error('Failed to load TradingView library'));
            document.head.appendChild(script);
          });

          console.log('[TradingViewChart] TradingView library loaded');
        }

        // Double-check container is still mounted
        if (!chartContainerRef.current) {
          throw new Error('Chart container not found');
        }

        // Debug logging for comparison charts
        console.log('[TradingViewChart] Chart initialization params:', {
          comparison,
          dataId,
          ticker,
          dataIdsRef: dataIdsRef.current,
          tickersRef: tickersRef.current,
          indicator
        });

        // Determine which datapack to load as the main symbol
        // For comparison charts, use the first datapack ID
        const mainDataId = (comparison && dataIdsRef.current && dataIdsRef.current.length > 0) ? dataIdsRef.current[0] : dataId;
        const mainTicker = (comparison && tickersRef.current && tickersRef.current.length > 0) ? tickersRef.current[0] : ticker;

        // Use friendly display name for all charts (both single-asset and comparison)
        const mainSymbol = mainTicker ? `${mainTicker} Historical Prices` : mainDataId;

        console.log('[TradingViewChart] Resolved main symbol:', { mainDataId, mainTicker, mainSymbol });

        // Initialize TradingView widget
        console.log(`[TradingViewChart] Initializing chart for symbol: ${mainSymbol}`);
        console.log(`[TradingViewChart] Is comparison chart: ${!!comparison}`);
        console.log('[TradingViewChart] Container element:', chartContainerRef.current);

        // Calculate initial visible range if dates are provided
        // Create and store datafeed reference for later access to cached bars
        const datafeed = new TradingViewDatafeed(
          mainDataId,
          mainTicker,
          sessionId,
          userId,
          dataIdsRef.current, // Pass all dataIds for comparison charts
          tickersRef.current, // Pass all tickers for comparison charts
          prefetchedDatapacks // Pass prefetched datapacks from context (historical sessions)
        );
        datafeedRef.current = datafeed; // Store reference for accessing cached bars later

        const widgetConfig: Record<string, unknown> = {
          // Container
          container: chartContainerRef.current,
          library_path: '/charting_library/',

          // Datafeed and symbol configuration
          // Pass all comparison dataIds and tickers to the datafeed for multi-symbol support
          datafeed: datafeed,
          symbol: mainSymbol, // Use friendly display name for comparison charts
          interval: '1D', // Default to daily view

          // Layout - specify explicit dimensions
          fullscreen: false,
          autosize: true,  // Let TradingView auto-size within the container
          height: 400,


          // Features to enable
          enabled_features: [
            'side_toolbar_in_fullscreen_mode', // Show toolbar in fullscreen
          ],

          // Features to disable
          disabled_features: [
            'use_localstorage_for_settings', // Avoid localStorage issues
            'volume_force_overlay', // Keep volume in separate pane
            'header_saveload', // Disable save/load (causes 404s without server)
            'save_chart_properties_to_local_storage', // Disable localStorage
            'timeframes_toolbar', // Disable timeframes
            'header_screenshot', // Disable screenshot
            'display_market_status',
            'header_symbol_search', // Disable symbol search
            'symbol_search_hot_key', // Disable search hotkey
            'header_resolutions', // Disable resolution picker
            'header_settings', // Disable settings
            'header_indicators', // Disable indicators button (backend controls indicators)
            'study_templates', // Disable study templates
          ],

          // Chart type based on prop
          // Style values: 0 = bars, 1 = candles, 2 = line, 3 = area, etc.
          // Note: In TradingView, candles (candlestick) = 1, not 0
          overrides: {
            'mainSeriesProperties.style': type === 'line' ? 2 : 1,
            'mainSeriesProperties.showCountdown': false,
            'scalesProperties.showSeriesLastValue': true,
            'scalesProperties.showStudyLastValue': true,
          },

          // Styling
          theme: 'light',
          locale: 'en',
        };

        const widget = new window.TradingView.widget(widgetConfig as never);

        widgetRef.current = widget;

        // Wait for chart to be ready
        widget.onChartReady(() => {
          console.log(`[TradingViewChart] Chart ready for dataId: ${dataId}`);

          // Note: We intentionally do NOT call setVisibleRange() here
          // Let TradingView auto-fit to the available data to avoid:
          // 1. Blank charts from date mismatches
          // 2. Unwanted whitespace from forced zoom levels
          // 3. Issues with historical data that doesn't extend to today
          // TradingView will automatically display all available data at an appropriate zoom level

          // Show chart once loaded
          setTimeout(() => {
            setState('success');
            console.log('[TradingViewChart] ✓ Chart ready and visible');
          }, 300);

          // Apply technical indicators if studies parameter is provided
          if (studies) {
            console.log(`[TradingViewChart] Applying studies: ${studies}`);

            // Parse comma-separated studies
            const studyList = studies.split(',').map(s => s.trim()).filter(Boolean);
            const chart = widget.activeChart();

            // Apply each study using shared studyNameMap
            for (const studyShortName of studyList) {
              const tvStudyName = studyNameMap[studyShortName];
              if (tvStudyName) {
                try {
                  chart.createStudy(tvStudyName, false, false);
                  console.log(`[TradingViewChart] ✓ Successfully applied: ${studyShortName} (${tvStudyName})`);
                } catch (error) {
                  console.error(`[TradingViewChart] ✗ Failed to apply ${studyShortName}:`, error);
                }
              } else {
                console.warn(`[TradingViewChart] ⚠ Unknown study: ${studyShortName}`);
              }
            }
          }

          // Draw support/resistance lines if provided
          if (supportResistanceRef.current) {
            console.log('[TradingViewChart] Drawing support/resistance lines...');
            try {
              const chart = widget.activeChart();
              drawSupportResistanceLines(chart, supportResistanceRef.current);
            } catch (error) {
              console.error('[TradingViewChart] Failed to draw S/R lines:', error);
            }
          }

          // Handle comparison charts (multi-asset price comparison only)
          // Note: TradingView Advanced Charts only supports symbol/price comparison, not indicator comparison
          if (comparison === 'price' && tickersRef.current && tickersRef.current.length > 1 && dataIdsRef.current && dataIdsRef.current.length > 1) {
            console.log(`[TradingViewChart] Rendering price comparison chart with ${tickersRef.current.length} assets`);
            renderComparisonChart(widget, comparison, tickersRef.current, dataIdsRef.current, indicator, sessionId, userId, contextDatapacks);
          } else if (comparison === 'indicator') {
            console.warn('[TradingViewChart] Indicator comparison is not supported by TradingView Advanced Charts');
          }
        });

      } catch (error) {
        console.error('[TradingViewChart] Failed to load chart:', error);
        setState('error');
        setErrorMessage(
          error instanceof Error ? error.message : 'Failed to load chart'
        );
      }
    };

    loadTradingView();

    // Cleanup on unmount
    return () => {
      if (widgetRef.current) {
        try {
          widgetRef.current.remove();
          widgetRef.current = null;
          console.log('[TradingViewChart] Widget removed');
        } catch (error) {
          console.error('[TradingViewChart] Error removing widget:', error);
        }
      }
    };
  }, [dataId, type, ticker, studies, sessionId, userId, startDate, endDate, comparison, tickersKey, dataIdsKey, indicator]);

  return (
    <ChartContainer>
      {/* Loading state */}
      {state === 'loading' && (
        <Skeleton className="h-[320px] w-full rounded-lg sm:h-[400px]" />
      )}

      {/* Error state */}
      {state === 'error' && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Failed to load chart
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>Data ID: {dataId}</p>
                {errorMessage && (
                  <p className="mt-1 text-xs">Error: {errorMessage}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chart container */}
      <div
        ref={chartContainerRef}
        className="h-[320px] w-full overflow-hidden rounded-lg border border-border bg-white shadow-sm sm:h-[400px]"
        style={{
          display: state === 'success' ? 'block' : 'none'
        }}
      />
    </ChartContainer>
  );
}
